"use client";

import { useState } from "react";

// ── Google Sheets webhook ─────────────────────────────────────────────────────
const WORKSHOP_SHEET_URL = "https://script.google.com/macros/s/AKfycbyGWFVGLUn3xNyxhz3OVqCRepc8d-KcH4aDJjjIXX7rssPB0V04GHOnYiKKTlJrDTYvvA/exec";

type SubmitStatus = "idle" | "loading" | "success" | "error";

export function RegisterForm() {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [error, setError]   = useState("");
  const [showOtherRole, setShowOtherRole] = useState(false);
  const [customRole, setCustomRole] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;

    const fullName = (form.elements.namedItem("fullName") as HTMLInputElement).value.trim();
    const email    = (form.elements.namedItem("email")    as HTMLInputElement).value.trim();
    const company  = (form.elements.namedItem("company")  as HTMLInputElement).value.trim();
    const role     = (form.elements.namedItem("role")     as HTMLSelectElement).value;
    
    // Use custom role if "Other" is selected
    const finalRole = role === "Other" ? customRole.trim() : role;

    // Validate required fields
    if (!fullName) {
      setError("Please enter your full name.");
      return;
    }

    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    // Validate custom role if "Other" is selected
    if (role === "Other" && !customRole.trim()) {
      setError("Please specify your role.");
      return;
    }

    setStatus("loading");
    setError("");

    try {
      const params = new URLSearchParams({
        "Full Name": fullName,
        Email:       email,
        Company:     company,
        Role:        finalRole,
        RegisteredAt: new Date().toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }),
      });

      await fetch(WORKSHOP_SHEET_URL, {
        method: "POST",
        mode:   "no-cors",
        body:   params,
      });

      setStatus("success");
      form.reset();
      setShowOtherRole(false);
      setCustomRole("");
    } catch {
      setStatus("error");
      setError("Something went wrong. Please try again.");
    }
  }

  if (status === "success") {
    return (
      <div
        role="status"
        style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          textAlign: "center", gap: 12, padding: "24px 0",
        }}
      >
        <div style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 56, height: 56, borderRadius: "50%",
          background: "rgba(34,197,94,0.12)", color: "var(--green)",
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <p style={{ fontSize: 16, fontWeight: 600, color: "var(--ink)", margin: 0 }}>
          You&rsquo;re registered!
        </p>
        <p style={{ fontSize: 14, color: "var(--ink-soft)", margin: 0 }}>
          Check your inbox for confirmation details.
        </p>
      </div>
    );
  }

  const isLoading = status === "loading";

  return (
    <form style={{ display: "flex", flexDirection: "column", gap: 14 }} onSubmit={handleSubmit} noValidate>
      <style>{`
        @media (max-width: 768px) {
          .register-form-wrapper input,
          .register-form-wrapper select {
            font-size: 16px !important;
            -webkit-appearance: none;
            appearance: none;
          }
          .register-form-wrapper label {
            font-size: 13px !important;
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <div className="register-form-wrapper">
        <label style={{ display: "block", fontSize: 12.5, color: "var(--ink-mute)", marginBottom: 6, fontFamily: "var(--font-jetbrains-mono), monospace" }}>
          Full Name <span style={{ color: "#f87171" }}>*</span>
        </label>
        <input
          name="fullName"
          type="text"
          placeholder="Jane Doe"
          required
          aria-required="true"
          style={{ 
            width: "100%", 
            padding: "12px 14px", 
            borderRadius: 10, 
            border: "1px solid var(--line-strong)", 
            background: "var(--paper-3)", 
            color: "var(--ink)", 
            fontSize: 14, 
            fontFamily: "inherit",
            boxSizing: "border-box"
          }}
        />
      </div>

      <div className="register-form-wrapper">
        <label style={{ display: "block", fontSize: 12.5, color: "var(--ink-mute)", marginBottom: 6, fontFamily: "var(--font-jetbrains-mono), monospace" }}>
          Work Email <span style={{ color: "#f87171" }}>*</span>
        </label>
        <input
          name="email"
          type="email"
          placeholder="jane@company.com"
          required
          aria-required="true"
          autoComplete="email"
          style={{ 
            width: "100%", 
            padding: "12px 14px", 
            borderRadius: 10, 
            border: "1px solid var(--line-strong)", 
            background: "var(--paper-3)", 
            color: "var(--ink)", 
            fontSize: 14, 
            fontFamily: "inherit",
            boxSizing: "border-box"
          }}
        />
      </div>

      <div className="register-form-wrapper">
        <label style={{ display: "block", fontSize: 12.5, color: "var(--ink-mute)", marginBottom: 6, fontFamily: "var(--font-jetbrains-mono), monospace" }}>
          Company
        </label>
        <input
          name="company"
          type="text"
          placeholder="Company name"
          autoComplete="organization"
          style={{ 
            width: "100%", 
            padding: "12px 14px", 
            borderRadius: 10, 
            border: "1px solid var(--line-strong)", 
            background: "var(--paper-3)", 
            color: "var(--ink)", 
            fontSize: 14, 
            fontFamily: "inherit",
            boxSizing: "border-box"
          }}
        />
      </div>

      <div className="register-form-wrapper">
        <label style={{ display: "block", fontSize: 12.5, color: "var(--ink-mute)", marginBottom: 6, fontFamily: "var(--font-jetbrains-mono), monospace" }}>
          Role
        </label>
        <select
          name="role"
          value={showOtherRole ? "Other" : undefined}
          onChange={(e) => {
            setShowOtherRole(e.target.value === "Other");
            if (e.target.value !== "Other") {
              setCustomRole("");
            }
          }}
          style={{ 
            width: "100%", 
            padding: "12px 14px", 
            borderRadius: 10, 
            border: "1px solid var(--line-strong)", 
            background: "var(--paper-3)", 
            color: "var(--ink)", 
            fontSize: 14, 
            fontFamily: "inherit",
            boxSizing: "border-box",
            cursor: "pointer"
          }}
        >
          <option>Supply Chain Leader</option>
          <option>Procurement / Sourcing</option>
          <option>S&amp;OP / Planning</option>
          <option>Logistics / Warehouse</option>
          <option>Supplier / SRM Manager</option>
          <option>Other</option>
        </select>
      </div>

      {/* Conditional custom role input */}
      {showOtherRole && (
        <div className="register-form-wrapper" style={{ 
          animation: "slideDown 0.2s ease-out"
        }}>
          <label style={{ display: "block", fontSize: 12.5, color: "var(--ink-mute)", marginBottom: 6, fontFamily: "var(--font-jetbrains-mono), monospace" }}>
            Please specify your role <span style={{ color: "#f87171" }}>*</span>
          </label>
          <input
            name="customRole"
            type="text"
            placeholder="e.g., Operations Manager, Supply Chain Consultant"
            value={customRole}
            onChange={(e) => setCustomRole(e.target.value)}
            required
            aria-required="true"
            style={{ 
              width: "100%", 
              padding: "12px 14px", 
              borderRadius: 10, 
              border: "1px solid var(--line-strong)", 
              background: "var(--paper-3)", 
              color: "var(--ink)", 
              fontSize: 14, 
              fontFamily: "inherit",
              boxSizing: "border-box"
            }}
          />
        </div>
      )}

      {status === "error" && (
        <p role="alert" style={{ fontSize: 13, color: "#f87171", margin: 0, textAlign: "center" }}>{error}</p>
      )}

      <button 
        type="submit" 
        className="btn btn-primary btn-lg" 
        style={{ 
          marginTop: 6, 
          width: "100%", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center" 
        }} 
        disabled={isLoading}
      >
        {isLoading ? "Registering…" : "Reserve My Free Seat"}
      </button>

      <p style={{ fontSize: 12, color: "var(--ink-mute)", textAlign: "center", margin: 0 }}>
        Free to attend. No credit card required.
      </p>
    </form>
  );
}
